import { useState } from 'react';
import { Tab } from '@headlessui/react';
import {
  UserPlusIcon,
  DocumentTextIcon,
  AcademicCapIcon,
  PlayCircleIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import ActiveResponseProcedures from '../components/CrisisResponse/ActiveResponseProcedures';

export default function Workflows() {
  const [categories] = useState([
    {
      id: 'onboarding',
      name: 'Onboarding',
      icon: UserPlusIcon,
      content: (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="space-y-6">
            <div className="border-b pb-4">
              <h3 className="text-lg font-medium">New Team Member Onboarding</h3>
              <div className="mt-4 space-y-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-blue-800 font-medium">1</span>
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="font-medium">Initial Documentation Review</p>
                    <p className="text-sm text-gray-500">Review crisis response documentation and protocols</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-blue-800 font-medium">2</span>
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="font-medium">Role Assignment</p>
                    <p className="text-sm text-gray-500">Assignment of specific responsibilities and access rights</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-blue-800 font-medium">3</span>
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="font-medium">Training Completion</p>
                    <p className="text-sm text-gray-500">Complete required training modules and assessments</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'response-plan',
      name: 'Response Plan Development',
      icon: DocumentTextIcon,
      content: (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="space-y-6">
            <div className="border-b pb-4">
              <h3 className="text-lg font-medium">Plan Development Stages</h3>
              <div className="mt-4 grid grid-cols-1 gap-4">
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium">Risk Assessment</h4>
                  <p className="text-sm text-gray-600 mt-2">Identify and analyze potential cyber threats and vulnerabilities</p>
                  <div className="mt-3 flex items-center">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-blue-600 h-2 rounded-full" style={{ width: '75%' }}></div>
                    </div>
                    <span className="ml-2 text-sm text-gray-600">75%</span>
                  </div>
                </div>
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium">Response Procedures</h4>
                  <p className="text-sm text-gray-600 mt-2">Define step-by-step response procedures for different scenarios</p>
                  <div className="mt-3 flex items-center">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-blue-600 h-2 rounded-full" style={{ width: '60%' }}></div>
                    </div>
                    <span className="ml-2 text-sm text-gray-600">60%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'training',
      name: 'Training & Drills',
      icon: AcademicCapIcon,
      content: (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="space-y-6">
            <div className="border-b pb-4">
              <h3 className="text-lg font-medium">Table Top Exercises</h3>
              <div className="mt-4 space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium">Ransomware Response Scenario</h4>
                  <p className="text-sm text-gray-600 mt-2">Practice response to a simulated ransomware attack</p>
                  <button className="mt-3 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                    Start Exercise
                  </button>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium">Data Breach Scenario</h4>
                  <p className="text-sm text-gray-600 mt-2">Simulate response to a major data breach incident</p>
                  <button className="mt-3 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                    Start Exercise
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'simulation',
      name: 'Simulation',
      icon: PlayCircleIcon,
      content: (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="space-y-6">
            <div className="border-b pb-4">
              <h3 className="text-lg font-medium">Live Simulation Exercises</h3>
              <div className="mt-4 grid grid-cols-1 gap-4">
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium">Full-Scale Crisis Exercise</h4>
                  <p className="text-sm text-gray-600 mt-2">
                    Comprehensive simulation involving all team members and departments
                  </p>
                  <div className="mt-4 flex space-x-4">
                    <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                      Schedule
                    </button>
                    <button className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'crisis-response',
      name: 'Crisis Response',
      icon: ShieldExclamationIcon,
      content: (
        <div className="bg-white p-6 rounded-lg shadow">
          <ActiveResponseProcedures />
        </div>
      ),
    },
  ]);

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Workflows</h2>
      <Tab.Group>
        <Tab.List className="flex space-x-4 border-b border-gray-200">
          {categories.map((category) => (
            <Tab
              key={category.id}
              className={({ selected }) =>
                clsx(
                  'flex items-center space-x-2 py-2 px-4 text-sm font-medium border-b-2 outline-none',
                  selected
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                )
              }
            >
              <category.icon className="h-5 w-5" />
              <span>{category.name}</span>
            </Tab>
          ))}
        </Tab.List>
        <Tab.Panels>
          {categories.map((category) => (
            <Tab.Panel key={category.id}>{category.content}</Tab.Panel>
          ))}
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
}